import { useQuery as _useQuery } from "@rocicorp/zero/react";
import { useEffect, useRef } from "react";
import {
  isTelemetryActive,
  registerQuery,
  trackQueryComplete,
  unregisterQuery,
} from "../lib/tracing";

function estimateSize(data: unknown): number {
  try {
    return JSON.stringify(data)?.length ?? 0;
  } catch {
    return 0;
  }
}

function rowCount(data: unknown): number {
  if (Array.isArray(data)) return data.length;
  if (data != null) return 1;
  return 0;
}

function extractQueryName(query: unknown): string {
  if (query == null) return "unknown";
  const q = query as Record<string, unknown>;
  if (q.query != null) {
    const inner = q.query as Record<string, unknown>;
    if (typeof inner.queryName === "string") return inner.queryName;
  }
  if (typeof q.queryName === "string") return q.queryName;
  return "unknown";
}

export const useQuery = ((...args: unknown[]) => {
  const result = (_useQuery as Function)(...args);
  const [data, details] = result;

  const mountTime = useRef(performance.now());
  const completed = useRef(false);
  // Track initial result type to detect cache hits: if the very first
  // observed type is 'complete', the data was already in Zero's client store.
  const initialType = useRef<string | undefined>(undefined);
  // First time `data` contained rows. Lets us separate "server response
  // arrived" from "type flipped to complete" — the gap between them is
  // purely client-side flush latency.
  const firstDataMs = useRef<number | undefined>(undefined);

  // Register/unregister for page load tracking
  useEffect(() => {
    registerQuery();
    return () => unregisterQuery();
  }, []);

  // Capture first committed type and first committed non-empty data. Done
  // in effects (not render) so discarded/interrupted renders don't skew
  // the timestamp, and refs only update for committed renders.
  useEffect(() => {
    if (details?.type && initialType.current === undefined) {
      initialType.current = details.type;
    }
    if (firstDataMs.current === undefined && rowCount(data) > 0) {
      firstDataMs.current = performance.now() - mountTime.current;
    }
  }, [details, data]);

  // Track first materialization only
  /* oxlint-disable react-hooks/exhaustive-deps -- args is stable by contract */
  useEffect(() => {
    if (!isTelemetryActive()) return;
    if (details?.type !== "complete") return;
    if (completed.current) return;
    completed.current = true;

    const name = extractQueryName(args[0]);
    const elapsed = performance.now() - mountTime.current;
    const rows = rowCount(data);
    const bytes = estimateSize(data);
    const cached = initialType.current === "complete";
    trackQueryComplete(name, elapsed, rows, bytes, cached, firstDataMs.current);
  }, [details, data, args[0]]);
  /* oxlint-enable react-hooks/exhaustive-deps */

  return result;
}) as typeof _useQuery;
